// packages/shared/src/services/chatService.ts

import { 
  doc, 
  addDoc, 
  getDoc,
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  limit,
  Timestamp 
} from 'firebase/firestore';
import { FirebaseService } from './firebaseService';
import { Chat, Message, Challenge } from '../types';

export class ChatService {
  private static instance: ChatService;
  private firebaseService: FirebaseService;

  private constructor() {
    this.firebaseService = FirebaseService.getInstance();
  }

  public static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  /**
   * Create a chat for an accepted challenge
   * This should only be called from ChallengeService when accepting a challenge
   */
  public async createChatForChallenge(challenge: Challenge): Promise<Chat> {
    const db = this.firebaseService.getFirestore();
    
    const expiresAt = new Date(challenge.startTime.getTime() + 48 * 60 * 60 * 1000);
    
    const chatData: Omit<Chat, 'id'> = {
      challengeId: challenge.id,
      participants: [challenge.fromUserId, challenge.toUserId],
      participantNames: [challenge.fromUserName, challenge.toUserName],
      matchTime: challenge.startTime,
      courtName: challenge.courtName,
      createdAt: new Date(),
      expiresAt: expiresAt,
      messageCount: 0
    };

    const docRef = await addDoc(collection(db, 'chats'), chatData);
    return { id: docRef.id, ...chatData };
  }

  /**
   * Get chat by challenge ID
   */
  public async getChatByChallenge(challengeId: string): Promise<Chat | null> {
    const db = this.firebaseService.getFirestore();
    
    const q = query(
      collection(db, 'chats'),
      where('challengeId', '==', challengeId),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    return {
      id: doc.id,
      ...data,
      matchTime: data.matchTime.toDate(),
      createdAt: data.createdAt.toDate(),
      expiresAt: data.expiresAt.toDate(),
      lastMessageAt: data.lastMessageAt?.toDate()
    } as Chat;
  }

  /**
   * Get all chats for a user
   */
public async getUserChats(userId: string): Promise<Chat[]> {
  const db = this.firebaseService.getFirestore();
  
  const q = query(
    collection(db, 'chats'),
    where('participants', 'array-contains', userId),
    orderBy('matchTime', 'desc')
  );
  
  const querySnapshot = await getDocs(q);
  
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      matchTime: data.matchTime.toDate(),
      createdAt: data.createdAt.toDate(),
      expiresAt: data.expiresAt.toDate(),
      lastMessageAt: data.lastMessageAt?.toDate()
    } as Chat;
  });
}

  /**
   * Send a message in a chat
   */
  public async sendMessage(
    chatId: string, 
    senderId: string, 
    senderName: string,
    content: string
  ): Promise<Message> {
    const db = this.firebaseService.getFirestore();
    
    // Validate chat exists and is active
    const chat = await this.getChatById(chatId);
    if (!chat) {
      throw new Error('Chat not found');
    }
    
    if (!this.isChatActive(chat)) {
      throw new Error('This chat has expired. Messages can only be sent up to 48 hours after the match time.');
    }
    
    if (!chat.participants.includes(senderId)) {
      throw new Error('You are not a participant in this chat');
    }
    
    // Validate message content
    if (!content.trim()) {
      throw new Error('Message content cannot be empty');
    }
    
    if (content.length > 500) {
      throw new Error('Message cannot exceed 500 characters');
    }
    
    const messageData: Omit<Message, 'id'> = {
      chatId,
      senderId,
      senderName,
      content: content.trim(),
      createdAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'messages'), messageData);
    return { id: docRef.id, ...messageData };
  }

  /**
   * Get messages for a chat
   */
  public async getMessages(chatId: string, messageLimit: number = 50): Promise<Message[]> {
    const db = this.firebaseService.getFirestore();
    
    const q = query(
      collection(db, 'messages'),
      where('chatId', '==', chatId),
      orderBy('createdAt', 'desc'),
      limit(messageLimit)
    );
    
    const querySnapshot = await getDocs(q);
    
    const messages = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt.toDate()
      } as Message;
    });
    
    // Return in chronological order (oldest first)
    return messages.reverse();
  }

  /**
   * Get chat by ID
   */
  public async getChatById(chatId: string): Promise<Chat | null> {
    const db = this.firebaseService.getFirestore();
    
    const docRef = doc(db, 'chats', chatId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      matchTime: data.matchTime.toDate(),
      createdAt: data.createdAt.toDate(),
      expiresAt: data.expiresAt.toDate(),
      lastMessageAt: data.lastMessageAt?.toDate()
    } as Chat;
  }

  /**
   * Check if a chat is still active (within 48 hours of match time)
   */
  public isChatActive(chat: Chat): boolean {
    const now = new Date();
    return now <= chat.expiresAt;
  }

  /**
   * Get the other participant in a chat
   */
  public getOtherParticipant(chat: Chat, currentUserId: string): { id: string; name: string } | null {
    const currentUserIndex = chat.participants.indexOf(currentUserId);
    if (currentUserIndex === -1) {
      return null;
    }
    
    const otherUserIndex = currentUserIndex === 0 ? 1 : 0;
    return {
      id: chat.participants[otherUserIndex],
      name: chat.participantNames[otherUserIndex]
    };
  }

  /**
   * Format time remaining until chat expires
   */
  public getTimeUntilExpiration(chat: Chat): string {
    const now = new Date();
    const msUntilExpiration = chat.expiresAt.getTime() - now.getTime();
    
    if (msUntilExpiration <= 0) {
      return 'Expired';
    }
    
    const hoursUntilExpiration = Math.floor(msUntilExpiration / (1000 * 60 * 60));
    const minutesUntilExpiration = Math.floor((msUntilExpiration % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hoursUntilExpiration > 0) {
      return `${hoursUntilExpiration}h ${minutesUntilExpiration}m`;
    } else {
      return `${minutesUntilExpiration}m`;
    }
  }
}

export default ChatService;